import * as types from "./actionTypes";
import * as setsApi from "../api/setsApi";
import { failureMessage, isForbidden, isNotFound } from "../api/client";
import { showInfo, showNotice } from "./noticeActions";
import { adoptCreatedSession } from "./sessionActions";
import { queueSongNext } from "./queueActions";
import { selectIsInSet, selectSetById } from "../selectors";
import { clampSemitones } from "../lib/preferences";
import { transposeNote } from "../lib/chords";

const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;

const failed = (error, what) =>
  showNotice(
    isForbidden(error)
      ? "Only a host with a host key can change sets."
      : isNotFound(error)
        ? "That set is no longer there."
        : failureMessage(error, `Couldn't ${what}. Check your connection and try again.`)
  );

let loadSequence = 0;

/** The sets of this device's host key (nothing without one). */
export const loadSets = () => async (dispatch, getState) => {
  if (!getState().host.hasKey) {
    dispatch({ type: types.SETS_CLEAR });
    return;
  }
  const sequence = ++loadSequence;
  try {
    const sets = await setsApi.list();
    if (sequence === loadSequence) dispatch({ type: types.SETS_LOAD_SUCCESS, sets });
  } catch (error) {
    if (sequence === loadSequence) dispatch({ type: types.SETS_LOAD_FAILED });
    if (!isForbidden(error)) console.error("Could not load the sets", error);
  }
};

export const setSaved = (set) => ({ type: types.SET_SAVED, set });
export const setRemoved = (setId) => ({ type: types.SET_REMOVED, setId });
export const markSetUsed = (setId) => ({ type: types.SETS_LAST_USED, setId });

/** Creates a set (from song ids, or from a session's queue). Resolves with it, or null. */
export const createSet = ({ name, songIds = [], fromSession = null }) => async (dispatch) => {
  try {
    const set = await setsApi.create({ name, songIds, fromSession });
    dispatch(setSaved(set));
    dispatch(markSetUsed(set.Id));
    return set;
  } catch (error) {
    dispatch(failed(error, "create the set"));
    return null;
  }
};

export const renameSet = (setId, name) => async (dispatch) => {
  try {
    dispatch(setSaved(await setsApi.rename(setId, name)));
    return true;
  } catch (error) {
    dispatch(failed(error, "rename the set"));
    return false;
  }
};

export const deleteSet = (setId) => async (dispatch) => {
  try {
    await setsApi.remove(setId);
  } catch (error) {
    if (!isNotFound(error)) {
      dispatch(failed(error, "delete the set"));
      return false;
    }
  }
  dispatch(setRemoved(setId));
  return true;
};

const withSongs = (set, songs) => ({ ...set, Songs: songs });

/**
 * Adds a song to a set (with the key this device sings it in); when that set is the one
 * on screens right now, the song joins the live set too. Resolves with true when added.
 */
export const addSongToSet = (setId, song, { semitones = 0, quiet = false } = {}) => async (dispatch, getState) => {
  const state = getState();
  const set = selectSetById(state, setId);
  if (!set) return false;
  try {
    const added = await setsApi.addSong(setId, song.Id);
    const transpose = clampSemitones(semitones);
    if (transpose) {
      await setsApi.setTranspose(setId, song.Id, transpose).catch(() => {});
      added.Transpose = transpose;
    }
    const songs = set.Songs.filter((item) => item.Id !== song.Id).concat([added]);
    dispatch(setSaved(withSongs(set, songs)));
    dispatch(markSetUsed(setId));
    const { session } = getState();
    if (session.isHost && session.SetId === setId && !session.ended && !state.songs[song.Id]) {
      dispatch(queueSongNext(session.Code, song.Id, { atEnd: true, semitones: transpose, quiet: true }));
    }
    if (!quiet) dispatch(showInfo(`Added to ${set.Name}`, { sub: song.Title }));
    return true;
  } catch (error) {
    dispatch(failed(error, "add the song to the set"));
    return false;
  }
};

export const removeSongFromSet = (setId, songId) => async (dispatch, getState) => {
  const set = selectSetById(getState(), setId);
  if (!set) return false;
  dispatch(setSaved(withSongs(set, set.Songs.filter((song) => song.Id !== songId))));
  try {
    await setsApi.removeSong(setId, songId);
    return true;
  } catch (error) {
    if (isNotFound(error)) return true;
    dispatch(setSaved(set));
    dispatch(failed(error, "remove the song from the set"));
    return false;
  }
};

/**
 * The bookmark button: one tap puts the song in a set with the key this device has for it,
 * with an undo. A song that is in the set already leaves everything as it is.
 */
export const bookmarkSong = (setId, song) => async (dispatch, getState) => {
  const state = getState();
  const set = selectSetById(state, setId);
  if (!set) {
    dispatch(showNotice("Pick a set first.", "info"));
    return false;
  }
  if (selectIsInSet(state, setId, song.Id)) {
    dispatch(showInfo(`Already in ${set.Name}`, { sub: "Nothing changed" }));
    return false;
  }
  const semitones = state.preferences.songKeys[song.Id] || 0;
  const position = set.Songs.length + 1;
  const key = song.Key ? ` · in ${transposeNote(song.Key, semitones)}` : "";
  const added = await dispatch(addSongToSet(setId, song, { semitones, quiet: true }));
  if (!added) return false;
  dispatch(showInfo(`Added to ${set.Name}`, { sub: `${ordinal(position)}${key}`, undo: () => removeSongFromSet(setId, song.Id) }));
  return true;
};

/** Moves a song one place up or down within a set. Applied optimistically. */
export const moveSongInSet = (setId, songId, direction) => async (dispatch, getState) => {
  const set = selectSetById(getState(), setId);
  if (!set) return;
  const order = set.Songs.map((song) => song.Id);
  const index = order.indexOf(songId);
  const target = index + (direction === "up" ? -1 : 1);
  if (index < 0 || target < 0 || target >= order.length) return;
  const next = [...order];
  [next[index], next[target]] = [next[target], next[index]];
  const reordered = next.map((id, position) => ({ ...set.Songs.find((song) => song.Id === id), Sort: position + 1 }));
  dispatch(setSaved(withSongs(set, reordered)));
  try {
    await setsApi.reorder(setId, next);
  } catch (error) {
    dispatch(setSaved(set));
    dispatch(failed(error, "reorder the set"));
  }
};

/** The key a song is sung in when the set is hosted. Applied optimistically. */
export const setSetTranspose = (setId, songId, semitones) => async (dispatch, getState) => {
  const set = selectSetById(getState(), setId);
  if (!set) return;
  const transpose = clampSemitones(semitones);
  dispatch(setSaved(withSongs(set, set.Songs.map((song) => (song.Id === songId ? { ...song, Transpose: transpose } : song)))));
  try {
    await setsApi.setTranspose(setId, songId, transpose);
  } catch (error) {
    dispatch(setSaved(set));
    dispatch(failed(error, "change the key"));
  }
};

/** Starts a session from a set and makes this device its host. Resolves with the session, or null. */
export const hostSet = (setId) => async (dispatch) => {
  try {
    const session = await setsApi.host(setId);
    dispatch(adoptCreatedSession(session));
    dispatch(markSetUsed(setId));
    dispatch(loadSets());
    return session;
  } catch (error) {
    dispatch(failed(error, "start the session"));
    return null;
  }
};
